import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import metafitLogo from '../assets/metafitlogo.png';
import { userApi } from '../config';
import { getProfileRouteForRole } from './instrutorProfile/role-utils';
import { ChevronDown, User, Heart, Settings, LogOut, X } from 'lucide-react';

interface userAuth {
    message: string;
    name?: string;
    fullName?: string;
    profileIcon?: string;
    role?: string;
}

const checkUserInfo = async () => {
    try {
        if (sessionStorage.getItem('userAccessToken')) {
            const res = await userApi.get('/check-user', { headers: { "Authorization": `Bearer ${sessionStorage.getItem('userAccessToken')}` } });
            if (res.status !== 200) {
                return { message: "User is not authenticated" };
            }
            return res.data as userAuth;
        } else if (sessionStorage.getItem('mentorAccessToken')) {
            const res = await userApi.get('/check-user', { headers: { "Authorization": `Bearer ${sessionStorage.getItem('mentorAccessToken')}` } });
            if (res.status !== 200) {
                return { message: "User is not authenticated" };
            }
            return res.data as userAuth;
        }
    } catch (err: any) {
        return { message: "User is not authenticated" };
    }
}

export default function Header() {
    const location = useLocation();
    const isHome = location.pathname === '/';
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const navRef = useRef<HTMLElement | null>(null);
    const profileDropdownRef = useRef<HTMLDivElement | null>(null);

    const [isUser, setIsUser] = useState({
        link1: "", link2: "",
        title1: "", title2: "", isAuth: false,
        name: "",
        fullName: "",
        profileIcon: ""
    });

    const [isPartner, setPartner] = useState({
        link1: "",
        title1: "", isAuth: false,
        name: "",
        fullName: "",
        profileIcon: "",
        profileLink: ""
    });

    const handleUserDetails = async () => {
        let cachedIcon = "";
        let cachedName = "";
        try {
            const rawCache = sessionStorage.getItem("userProfileCache");
            if (rawCache) {
                const parsed = JSON.parse(rawCache);
                if (parsed?.data?.user) {
                    const u = parsed.data.user;
                    cachedIcon = u.profileIcon && u.profileIcon !== "undefined" ? u.profileIcon : "";
                    cachedName = [u.firstname, u.lastname].filter(Boolean).join(" ");
                }
            }
        } catch (e) {}

        const userInfo = await checkUserInfo();
        if (userInfo && userInfo["message"] === "User is authenticated") {
            let finalName = userInfo.fullName || cachedName || userInfo.name || "";
            let finalIcon = userInfo.profileIcon || cachedIcon || "";

            // If profileIcon is not yet available, fetch from /get-user-profile
            if (!finalIcon && sessionStorage.getItem("userAccessToken")) {
                try {
                    const res = await userApi.get<any>("/get-user-profile", {
                        headers: { "Authorization": `Bearer ${sessionStorage.getItem("userAccessToken")}` }
                    });
                    if (res?.data?.user) {
                        const u = res.data.user;
                        if (u.profileIcon && u.profileIcon !== "undefined") {
                            finalIcon = u.profileIcon;
                        }
                        const fetchedFullName = [u.firstname, u.lastname].filter(Boolean).join(" ");
                        if (fetchedFullName) finalName = fetchedFullName;
                    }
                } catch (e) {}
            }

            setIsUser({
                link1: "/profile",
                link2: "/logout",
                title1: "Profile",
                title2: "Logout",
                isAuth: true,
                name: userInfo.name || "",
                fullName: finalName,
                profileIcon: finalIcon
            });
        } else if (userInfo && userInfo["message"] === "Instructor is authenticated" && userInfo.role) {
            setPartner({
                link1: "/mentor-profile",
                title1: "Mentor Profile",
                isAuth: true,
                name: userInfo.name || "",
                fullName: userInfo.fullName || userInfo.name || "Instructor",
                profileIcon: userInfo.profileIcon || "",
                profileLink: getProfileRouteForRole(userInfo.role)
            });
        }
    }

    useEffect(() => {
        handleUserDetails();

        const handleAuthChange = () => {
            handleUserDetails();
        };

        const handleScroll = () => {
            setScrolled(window.scrollY > 10);
        };

        const handleResize = () => {
            if (window.innerWidth > 900) {
                setIsMenuOpen(false);
            }
        };

        const handleClickAway = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node | null;
            if (!target) return;

            if (profileDropdownRef.current && !profileDropdownRef.current.contains(target)) {
                setIsProfileDropdownOpen(false);
            }

            if (!isMenuOpen) return;
            const menuToggle = navRef.current?.querySelector('.menu-toggle');
            const navLinks = navRef.current?.querySelector('.nav-links');

            if (navLinks?.contains(target) || menuToggle?.contains(target)) {
                return;
            }

            setIsMenuOpen(false);
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsMenuOpen(false);
                setIsProfileDropdownOpen(false);
            }
        };

        window.addEventListener('auth-change', handleAuthChange);
        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleResize);
        document.addEventListener('mousedown', handleClickAway);
        document.addEventListener('touchstart', handleClickAway);
        document.addEventListener('keydown', handleEscape);

        document.body.style.overflow = isMenuOpen ? 'hidden' : '';
        if (isMenuOpen) {
            document.body.classList.add('mobile-menu-open');
        } else {
            document.body.classList.remove('mobile-menu-open');
        }

        return () => {
            window.removeEventListener('auth-change', handleAuthChange);
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('mousedown', handleClickAway);
            document.removeEventListener('touchstart', handleClickAway);
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = '';
            document.body.classList.remove('mobile-menu-open');
        };
    }, [isMenuOpen]);

    const handleLogout = async () => {
        sessionStorage.clear();
        window.location.reload();
    }

    return (
        <header ref={navRef} className={`site ${isHome ? 'is-home' : 'not-home'} ${scrolled ? 'scrolled' : ''}`}>
            <div className="wrap">
                <nav>
                    <Link to="/" className="logo flex items-center">
                        <img className="h-12 sm:h-14 w-auto object-contain" src={metafitLogo} alt="Metafit Wellness" />
                    </Link>

                    <ul className={`nav-links ${isMenuOpen ? 'open' : ''}`}>
                        <li className="flex items-center justify-between pb-2 mb-1 border-b border-gray-200/60 md:hidden">
                            <span className="text-xs font-bold tracking-wider uppercase text-gray-500">Navigation</span>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsMenuOpen(false);
                                }}
                                onTouchEnd={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsMenuOpen(false);
                                }}
                                className="p-2 rounded-full hover:bg-gray-200/70 active:bg-gray-300 text-gray-700 transition-colors cursor-pointer select-none"
                                aria-label="Close menu"
                            >
                                <X className="w-5 h-5 pointer-events-none" />
                            </button>
                        </li>
                        <li><Link to="/" onClick={() => setIsMenuOpen(false)}>Home</Link></li>
                        <li><Link to="/services" onClick={() => setIsMenuOpen(false)}>Services</Link></li>
                        <li><Link to="/about" onClick={() => setIsMenuOpen(false)}>About Us</Link></li>
                        <li><Link to="/classes" onClick={() => setIsMenuOpen(false)}>Book Classes</Link></li>
                        <li><Link to="/contact" onClick={() => setIsMenuOpen(false)}>Contact Us</Link></li>
                        <li><Link to="/blog" onClick={() => setIsMenuOpen(false)}>Blog</Link></li>
                        <li>
                            <a
                                href="https://mwellnessbazaar.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                Shop
                            </a>
                        </li>
                        <li className="md:hidden"><Link to="/ai-diet-nutrition" onClick={() => setIsMenuOpen(false)}>AI DIET NUTRITION</Link></li>
                        <li className="nav-links-cta md:hidden">
                            <Link
                                to={isUser.isAuth ? "/profile" : isPartner.isAuth ? isPartner.profileLink : "/trainer-signup"}
                                onClick={() => setIsMenuOpen(false)}
                                className="btn btn-partner bg-black text-white hover:bg-neutral-800"
                            >
                                {isUser.isAuth ? "USER PROFILE" : isPartner.isAuth ? "PARTNER PROFILE" : "Become a Partner"}
                            </Link>
                        </li>
                        <li className="nav-links-auth md:hidden">
                            {isUser.isAuth || isPartner.isAuth ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsMenuOpen(false);
                                        handleLogout();
                                    }}
                                    className="nav-logout"
                                >
                                    Logout
                                </button>
                            ) : (
                                <Link to="/login" className="nav-login-btn" onClick={() => setIsMenuOpen(false)}>
                                    <span>User Login</span>
                                </Link>
                            )}
                        </li>
                    </ul>

                    <div className="nav-cta flex items-center gap-4">
                        {!isPartner.isAuth && (
                            <Link to="/trainer-signup" className="btn btn-partner font-semibold px-5 py-2.5 text-sm rounded-full bg-black text-white hover:bg-neutral-800 hover:text-white transition-all shadow-sm" onClick={() => setIsMenuOpen(false)}>
                                Become a Partner
                            </Link>
                        )}
                        {isUser.isAuth ? (
                            <div className="relative" ref={profileDropdownRef}>
                                <button
                                    onClick={() => setIsProfileDropdownOpen(prev => !prev)}
                                    className="flex items-center gap-2.5 pl-1.5 pr-3.5 py-1.5 rounded-full border border-gray-200/90 bg-white hover:bg-gray-50 transition-all shadow-[0_1px_4px_rgba(0,0,0,0.04)] hover:shadow-md cursor-pointer select-none"
                                >
                                    {isUser.profileIcon ? (
                                        <img
                                            src={isUser.profileIcon}
                                            alt={isUser.fullName || isUser.name}
                                            className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover shrink-0 ring-1 ring-gray-200"
                                        />
                                    ) : (
                                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-teal-100 text-teal-800 flex items-center justify-center font-bold text-xs shrink-0 ring-1 ring-teal-200">
                                            {(isUser.fullName || isUser.name || "U").charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <span className="text-xs sm:text-sm font-semibold text-gray-800 truncate max-w-[130px]">
                                        {isUser.fullName || (isUser.name ? (isUser.name.charAt(0).toUpperCase() + isUser.name.slice(1)) : "User")}
                                    </span>
                                    <ChevronDown className={`w-3.5 h-3.5 text-gray-600 transition-transform duration-200 shrink-0 ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Dropdown Menu */}
                                {isProfileDropdownOpen && (
                                    <div className="absolute right-0 mt-2 w-60 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-[1000] animate-in fade-in slide-in-from-top-2 duration-150">
                                        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3">
                                            {isUser.profileIcon ? (
                                                <img
                                                    src={isUser.profileIcon}
                                                    alt="Profile"
                                                    className="w-10 h-10 rounded-full object-cover shrink-0 ring-1 ring-gray-100"
                                                />
                                            ) : (
                                                <div className="w-10 h-10 rounded-full bg-teal-100 text-teal-800 flex items-center justify-center font-bold text-sm shrink-0">
                                                    {(isUser.fullName || isUser.name || "U").charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-bold text-gray-900 truncate">
                                                    {isUser.fullName || isUser.name || "User"}
                                                </p>
                                                <p className="text-[11px] text-teal-700 font-medium">Active Member</p>
                                            </div>
                                        </div>

                                        <div className="py-1.5">
                                            <Link
                                                to="/profile"
                                                onClick={() => setIsProfileDropdownOpen(false)}
                                                className="flex items-center gap-3 px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-800 transition-colors"
                                            >
                                                <User className="w-4 h-4 text-gray-400" />
                                                <span>My Profile</span>
                                            </Link>
                                            <Link
                                                to="/profile?tab=wishlist"
                                                onClick={() => setIsProfileDropdownOpen(false)}
                                                className="flex items-center gap-3 px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-800 transition-colors"
                                            >
                                                <Heart className="w-4 h-4 text-gray-400" />
                                                <span>Saved / Wishlist</span>
                                            </Link>
                                            <Link
                                                to="/profile/user-profile-settings"
                                                onClick={() => setIsProfileDropdownOpen(false)}
                                                className="flex items-center gap-3 px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-800 transition-colors"
                                            >
                                                <Settings className="w-4 h-4 text-gray-400" />
                                                <span>Settings</span>
                                            </Link>
                                        </div>

                                        <div className="pt-1.5 border-t border-gray-100">
                                            <button
                                                onClick={() => {
                                                    setIsProfileDropdownOpen(false);
                                                    handleLogout();
                                                }}
                                                className="w-full flex items-center gap-3 px-4 py-2 text-xs sm:text-sm font-medium text-red-600 hover:bg-red-50 transition-colors text-left cursor-pointer"
                                            >
                                                <LogOut className="w-4 h-4 text-red-500" />
                                                <span>Logout</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : isPartner.isAuth ? (
                            <div className="relative" ref={profileDropdownRef}>
                                <button
                                    onClick={() => setIsProfileDropdownOpen(prev => !prev)}
                                    className="flex items-center gap-2.5 pl-1.5 pr-3.5 py-1.5 rounded-full border border-gray-200/90 bg-white hover:bg-gray-50 transition-all shadow-[0_1px_4px_rgba(0,0,0,0.04)] hover:shadow-md cursor-pointer select-none"
                                >
                                    {isPartner.profileIcon ? (
                                        <img
                                            src={isPartner.profileIcon}
                                            alt={isPartner.fullName || isPartner.name}
                                            className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover shrink-0 ring-1 ring-gray-200"
                                        />
                                    ) : (
                                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-teal-100 text-teal-800 flex items-center justify-center font-bold text-xs shrink-0 ring-1 ring-teal-200">
                                            {(isPartner.fullName || isPartner.name || "P").charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <span className="text-xs sm:text-sm font-semibold text-gray-800 truncate max-w-[130px]">
                                        {isPartner.fullName || isPartner.name || "Partner"}
                                    </span>
                                    <ChevronDown className={`w-3.5 h-3.5 text-gray-600 transition-transform duration-200 shrink-0 ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {isProfileDropdownOpen && (
                                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-[1000] animate-in fade-in slide-in-from-top-2 duration-150">
                                        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3">
                                            {isPartner.profileIcon ? (
                                                <img
                                                    src={isPartner.profileIcon}
                                                    alt="Profile"
                                                    className="w-10 h-10 rounded-full object-cover shrink-0 ring-1 ring-gray-100"
                                                />
                                            ) : (
                                                <div className="w-10 h-10 rounded-full bg-teal-100 text-teal-800 flex items-center justify-center font-bold text-sm shrink-0">
                                                    {(isPartner.fullName || isPartner.name || "P").charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-bold text-gray-900 truncate">
                                                    {isPartner.fullName || isPartner.name || "Partner"}
                                                </p>
                                                <p className="text-[11px] text-teal-700 font-medium">Partner Portal</p>
                                            </div>
                                        </div>

                                        <div className="py-1.5">
                                            <Link
                                                to={isPartner.profileLink}
                                                onClick={() => setIsProfileDropdownOpen(false)}
                                                className="flex items-center gap-3 px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-800 transition-colors"
                                            >
                                                <User className="w-4 h-4 text-gray-400" />
                                                <span>Dashboard</span>
                                            </Link>
                                        </div>

                                        <div className="pt-1.5 border-t border-gray-100">
                                            <button
                                                onClick={() => {
                                                    setIsProfileDropdownOpen(false);
                                                    handleLogout();
                                                }}
                                                className="w-full flex items-center gap-3 px-4 py-2 text-xs sm:text-sm font-medium text-red-600 hover:bg-red-50 transition-colors text-left cursor-pointer"
                                            >
                                                <LogOut className="w-4 h-4 text-red-500" />
                                                <span>Logout</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                <Link to="/login" className="btn btn-ghost" onClick={() => setIsMenuOpen(false)}>
                                    JOIN NOW
                                </Link>
                            </>
                        )}

                        {/* Mobile User Profile/Login Icon next to menu toggle */}
                        <div className="mobile-user-btn-wrapper">
                            {isUser.isAuth || isPartner.isAuth ? (
                                <Link
                                    to={isUser.isAuth ? "/profile" : isPartner.profileLink}
                                    className="mobile-user-avatar-btn"
                                    aria-label="User Profile"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    {(isUser.isAuth ? isUser.profileIcon : isPartner.profileIcon) ? (
                                        <img
                                            src={isUser.isAuth ? isUser.profileIcon : isPartner.profileIcon}
                                            alt={isUser.fullName || isUser.name || "User"}
                                            className="w-full h-full object-cover rounded-[10px]"
                                        />
                                    ) : (
                                        <div className="w-full h-full rounded-[10px] bg-teal-100 text-teal-800 flex items-center justify-center font-bold text-sm">
                                            {((isUser.isAuth ? (isUser.fullName || isUser.name) : (isPartner.fullName || isPartner.name)) || "U").charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                </Link>
                            ) : (
                                <Link
                                    to="/login"
                                    className="mobile-user-avatar-btn mobile-user-logged-out"
                                    aria-label="User Login"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    <User className="w-5 h-5" />
                                </Link>
                            )}
                        </div>

                        <button
                            className={`menu-toggle ${isMenuOpen ? 'open' : ''}`}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsMenuOpen(prev => !prev);
                            }}
                            onTouchEnd={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsMenuOpen(prev => !prev);
                            }}
                            aria-label="Toggle menu"
                        >
                            <span></span><span></span><span></span>
                        </button>
                    </div>
                </nav>
            </div>
            {isMenuOpen && (
                <button
                    type="button"
                    className="nav-backdrop"
                    aria-label="Close menu"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsMenuOpen(false);
                    }}
                    onTouchEnd={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsMenuOpen(false);
                    }}
                />
            )}
        </header>
    );
}
